/** Prematch player props (Decimal 910/912/914) — hide once the live innings has started. */
export const PREMATCH_BATSMAN_TYPE_IDS = new Set(["910", "912", "914"]);
/** In-play batter lines Decimal keeps offering after a wicket until they flip display. */
export const INPLAY_BATSMAN_LINE_TYPE_IDS = new Set(["710", "720", "730", "760"]);

const DECIMAL_BATTER_DISMISSAL_RE =
  /^(Caught|Bowled|Stumped|LBW|Run Out|Hit Wicket|Handled the Ball|Obstructing the Field|Timed Out)\b/i;

export function isPrematchBatsmanPropMarket(market) {
  return PREMATCH_BATSMAN_TYPE_IDS.has(String(market?.market_type_id ?? "").trim());
}

export function isInPlayBatsmanLineMarket(market) {
  return INPLAY_BATSMAN_LINE_TYPE_IDS.has(String(market?.market_type_id ?? "").trim());
}

export function isDecimalBatterDismissedStatus(status) {
  const s = String(status ?? "").trim();
  if (!s || /retired/i.test(s)) return false;
  return DECIMAL_BATTER_DISMISSAL_RE.test(s);
}

function normalizeBatterNameKey(raw) {
  return String(raw ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** True once Decimal has left Pre Match Service, or live balls/innings are present. */
export function isTraderCricketMatchInPlay({ prematch, curBall } = {}) {
  if (prematch === false) return true;
  const ball = Number.parseFloat(curBall);
  return Number.isFinite(ball) && ball > 0;
}

export function batsmanMarketPlayerName(market) {
  const raw = String(market?.market_name || market?.name || "").trim();
  if (!raw) return "";
  const commaIdx = raw.indexOf(",");
  let player = commaIdx >= 0 ? raw.slice(commaIdx + 1).trim() : raw;
  player = player.replace(/\s+to reach\s+\d+(\.\d+)?\s+runs?\s*$/i, "").trim();
  player = player.replace(/\s+(runs|fours|sixes)\s*$/i, "").trim();
  return player;
}

function scorecardDismissedBatterIndex(scorecard) {
  const ids = new Set();
  const names = new Set();
  if (!scorecard || typeof scorecard !== "object") return { ids, names };
  for (const key of ["inns1", "inns2", "inns3", "inns4"]) {
    const batting = scorecard[key]?.batting;
    if (!Array.isArray(batting)) continue;
    for (const row of batting) {
      if (!isDecimalBatterDismissedStatus(row?.status)) continue;
      const id = String(row?.id ?? "").trim();
      if (id) ids.add(id);
      const name = normalizeBatterNameKey(row?.name);
      if (name) names.add(name);
    }
  }
  return { ids, names };
}

export function isBatsmanLineForDismissedBatter(market, scorecard) {
  if (!isInPlayBatsmanLineMarket(market)) return false;
  const dismissed = scorecardDismissedBatterIndex(scorecard);
  const pid = String(market?.player_id ?? "").trim();
  if (pid && dismissed.ids.has(pid)) return true;
  const pname = normalizeBatterNameKey(batsmanMarketPlayerName(market));
  return Boolean(pname && dismissed.names.has(pname));
}

/**
 * Hide prematch player props once live, and hide in-play batter lines after the scorecard dismissal.
 * Decimal often leaves type 710 `display=1` for several balls after a wicket.
 */
export function getTraderBatsmanMarketHideReason(market, { prematch, scorecard, innsNow, curBall } = {}) {
  if (!market) return null;
  if (isPrematchBatsmanPropMarket(market) && isTraderCricketMatchInPlay({ prematch, innsNow, curBall })) {
    return "Prematch player markets are not offered after the match has started.";
  }
  if (isBatsmanLineForDismissedBatter(market, scorecard)) {
    return "This batter is out. Trading is closed.";
  }
  return null;
}
